import {
  InternalApiEntityGroup,
  InternalApiEntityType,
  InternalApiScenarioIntervalTimeUnit,
  InternalApiScenarioLanguage,
} from '../../../api-internal';
import {
  DatePicker,
  SalvFieldHeader,
  SalvNumberField,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  SalvTextArea,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { convertToDropdownOption, DATE_TIME_FORMAT } from '../../../common/helper';
import dayjs from 'dayjs';
import React from 'react';
import { useFormikContext } from 'formik';
import { ScenarioVersionModalFormValues } from './ScenarioVersionModal';
import './ScenarioVersionLeftPanel.scss';
import ParametersEditor from './ParametersEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import TriggeringSegments from '../../segments/TriggeringSegments';

interface ScenarioVersionLeftPanelProps {
  isPeriodic: boolean;
  isPeriodicEntity: boolean;
  isTransactionScenario: boolean;
  isDraft: boolean;
  isDisabled: boolean;
}

export const toOptions = (
  segments: Array<InternalApiEntityGroup> | undefined,
  entityType: InternalApiEntityType
): DropdownOption[] =>
  segments
    ?.filter((segment) => segment.entityType === entityType)
    ?.map((segment) => convertToDropdownOption(segment.name, segment.code, segment.archivedAt !== undefined)) ?? [];

const ScenarioVersionLeftPanel = ({
  isPeriodic,
  isPeriodicEntity,
  isTransactionScenario,
  isDraft,
  isDisabled,
}: ScenarioVersionLeftPanelProps) => {
  const { setFieldValue, values, errors } = useFormikContext<ScenarioVersionModalFormValues>();

  return (
    <div className='scenario-version-left-panel'>
      {values.language === InternalApiScenarioLanguage.SQL && <ParametersEditor disabled={isDisabled} />}

      {!isPeriodic && (
        <TriggeringSegments
          isDisabled={isDisabled}
          isTransactionScenario={isTransactionScenario}
          isPeriodicEntity={isPeriodicEntity}
        />
      )}

      {(isPeriodic || isPeriodicEntity) && (
        <>
          <div>
            <SalvFieldHeader
              label='Periodic scenario interval'
              description='How often will the scenario run'
              size={Size.MEDIUM}
            />
            <div className='periodic-interval'>
              <SalvNumberField
                onChange={(value) => {
                  setFieldValue('periodicInterval', value);
                }}
                ariaLabel='Periodic scenario interval'
                value={values.periodicInterval}
                minValue={1}
                size={Size.MEDIUM}
                className='periodic-interval-field'
                isInvalid={!!errors.periodicInterval || !!errors.periodicIntervalTimeUnit}
                disabled={isDisabled}
              />
              <SalvRadioToggleGroup
                value={values.periodicIntervalTimeUnit}
                onChange={(periodicIntervalTimeUnit) => {
                  if (isDraft) {
                    setFieldValue('periodicIntervalTimeUnit', periodicIntervalTimeUnit);
                  }
                }}
                size={Size.MEDIUM}
                disabled={isDisabled}
                ariaLabel='Periodic scenario interval time unit'
              >
                {!isPeriodicEntity && (
                  <SalvRadioToggleButton value={InternalApiScenarioIntervalTimeUnit.HOUR} label='Hour' />
                )}
                <SalvRadioToggleButton value={InternalApiScenarioIntervalTimeUnit.DAY} label='Day' />
                <SalvRadioToggleButton value={InternalApiScenarioIntervalTimeUnit.WEEK} label='Week' />
                <SalvRadioToggleButton value={InternalApiScenarioIntervalTimeUnit.MONTH} label='Month' />
              </SalvRadioToggleGroup>
            </div>
          </div>

          <div className='start-time-field'>
            <SalvFieldHeader label='Periodic scenario start time' size={Size.MEDIUM} />
            <DatePicker
              onChange={(startTime) => {
                setFieldValue('startTime', startTime?.format(DATE_TIME_FORMAT));
              }}
              min={dayjs()}
              value={
                dayjs(values.startTime, DATE_TIME_FORMAT).isValid() ? dayjs(values.startTime, DATE_TIME_FORMAT) : null
              }
              size={Size.MEDIUM}
              className='start-time-field'
              showTime={true}
              showDefaultOptions={false}
              isInvalid={!!errors.startTime}
              disabled={isDisabled}
            />
          </div>
        </>
      )}

      <SalvNumberField
        label={
          <div className='d-flex align-items-center'>
            <span className='m-r-8'>Alert suppression interval</span>
            <SalvTooltipWrapper
              tooltip={`
              After an alert is triggered, further alerts for the same scenario and person are paused for the set number of days. 
              This pause only applies to the specific scenario and person`}
              placement='right'
            >
              <FontAwesomeIcon
                icon={faCircleInfo}
                className='text-muted cursor-pointer'
                data-testid='description-icon'
              />
            </SalvTooltipWrapper>
          </div>
        }
        onChange={(value) => {
          setFieldValue('suppressionInterval', value);
        }}
        value={values.suppressionInterval}
        minValue={0}
        size={Size.MEDIUM}
        placeholder='Days'
        description='Set days to suppress further alerts for a person after an alert is generated'
        className='suppression-interval-field'
        isInvalid={!!errors.suppressionInterval}
        disabled={isDisabled}
        ariaLabel='Alert suppression interval'
      />

      <SalvTextArea
        label='Note'
        description='Comment on what is changing in this version'
        placeholder='Note'
        size={Size.MEDIUM}
        onChange={(note) => setFieldValue('note', note)}
        value={values.note}
        autoSizing={true}
        minRows={4}
        isInvalid={!!errors.note}
        className='note-field'
        disabled={isDisabled}
      />
    </div>
  );
};

export default ScenarioVersionLeftPanel;
